import {apiService} from '@/core/api';
import {API_ENDPOINTS} from '@/core/constants/endpoints';
import {IdeasApiResponse, IdeasFilters, Idea, IdeaInvitationsResponse} from '../model/types';
import {CreateIdeaPayload} from '@/features/calls/model/types';
import {AxiosRequestConfig} from 'axios';

class IdeasService {
  async getAllIdeas(filters?: IdeasFilters, type?: string | null): Promise<IdeasApiResponse> {
    const params: any = {};

    if (filters?.search) params.search = filters.search;
    if (filters?.sort) params.sort = encodeURIComponent(filters.sort);
    if (filters?.page) params.page = filters.page;
    if (filters?.limit) params.limit = filters.limit;
    if (type) params.type = type;

    // Add nested filters with URL encoding
    if (filters?.filters) {
      if (filters.filters.fromDate) {
        params.from_date = encodeURIComponent(filters.filters.fromDate);
      }
      if (filters.filters.toDate) {
        params.to_date = encodeURIComponent(filters.filters.toDate);
      }
      if (filters.filters.categories?.length) {
        params.categories = encodeURIComponent(filters.filters.categories.join(','));
      }
      if (filters.filters.programmes?.length) {
        params.programmes = filters.filters.programmes.join(','); // Send as comma-separated tids
      }
      // Send tids as comma-separated strings for sectors, expertise, and looking_for
      if (filters.filters.sectors?.length) {
        params.sectors = filters.filters.sectors.join(','); // Send as comma-separated tids
      }
      if (filters.filters.expertise?.length) {
        params.expertise = filters.filters.expertise.join(','); // Send as comma-separated tids
      }
      if (filters.filters.lookingFor?.length) {
        params.looking_for = filters.filters.lookingFor.join(','); // Send as comma-separated tids
      }
    }

    return await apiService.get<IdeasApiResponse>(API_ENDPOINTS.IDEAS.GET_ALL, {params});
  }
  async getPublishedIdeas(filters?: IdeasFilters, type?: string | null): Promise<IdeasApiResponse> {
    const params: any = {};

    if (filters?.search) params.search = filters.search;
    if (filters?.sort) params.sort = encodeURIComponent(filters.sort);
    if (filters?.page) params.page = filters.page;
    if (filters?.limit) params.limit = filters.limit;
    if (type) params.type = type;

    // Add nested filters with URL encoding
    if (filters?.filters) {
      if (filters.filters.fromDate) {
        params.from_date = encodeURIComponent(filters.filters.fromDate);
      }
      if (filters.filters.toDate) {
        params.to_date = encodeURIComponent(filters.filters.toDate);
      }
      if (filters.filters.categories?.length) {
        params.categories = encodeURIComponent(filters.filters.categories.join(','));
      }
      if (filters.filters.programmes?.length) {
        params.programmes = filters.filters.programmes.join(','); // Send as comma-separated tids
      }
      // Send tids as comma-separated strings for sectors, expertise, and looking_for
      if (filters.filters.sectors?.length) {
        params.sectors = filters.filters.sectors.join(','); // Send as comma-separated tids
      }
      if (filters.filters.expertise?.length) {
        params.expertise = filters.filters.expertise.join(','); // Send as comma-separated tids
      }
      if (filters.filters.lookingFor?.length) {
        params.looking_for = filters.filters.lookingFor.join(','); // Send as comma-separated tids
      }
    }

    return await apiService.get<IdeasApiResponse>(API_ENDPOINTS.IDEAS.GET_ALL, {params});
  }
  async getDraftIdeas(filters?: IdeasFilters, type?: string | null): Promise<IdeasApiResponse> {
    const params: any = {};

    if (filters?.search) params.search = filters.search;
    if (filters?.sort) params.sort = encodeURIComponent(filters.sort);
    if (filters?.page) params.page = filters.page;
    if (filters?.limit) params.limit = filters.limit;
    if (type) params.type = type;

    // Add nested filters with URL encoding
    if (filters?.filters) {
      if (filters.filters.fromDate) {
        params.from_date = encodeURIComponent(filters.filters.fromDate);
      }
      if (filters.filters.toDate) {
        params.to_date = encodeURIComponent(filters.filters.toDate);
      }
      if (filters.filters.categories?.length) {
        params.categories = encodeURIComponent(filters.filters.categories.join(','));
      }
      if (filters.filters.programmes?.length) {
        params.programmes = filters.filters.programmes.join(','); // Send as comma-separated tids
      }
      // Send tids as comma-separated strings for sectors, expertise, and looking_for
      if (filters.filters.sectors?.length) {
        params.sectors = filters.filters.sectors.join(','); // Send as comma-separated tids
      }
      if (filters.filters.expertise?.length) {
        params.expertise = filters.filters.expertise.join(','); // Send as comma-separated tids
      }
      if (filters.filters.lookingFor?.length) {
        params.looking_for = filters.filters.lookingFor.join(','); // Send as comma-separated tids
      }
    }

    return await apiService.get<IdeasApiResponse>(API_ENDPOINTS.IDEAS.GET_ALL, {params});
  }
  async getArchivedIdeas(filters?: IdeasFilters, type?: string | null): Promise<IdeasApiResponse> {
    const params: any = {};

    if (filters?.search) params.search = filters.search;
    if (filters?.sort) params.sort = encodeURIComponent(filters.sort);
    if (filters?.page) params.page = filters.page;
    if (filters?.limit) params.limit = filters.limit;
    if (type) params.type = type;

    // Add nested filters with URL encoding
    if (filters?.filters) {
      if (filters.filters.fromDate) {
        params.from_date = encodeURIComponent(filters.filters.fromDate);
      }
      if (filters.filters.toDate) {
        params.to_date = encodeURIComponent(filters.filters.toDate);
      }
      if (filters.filters.categories?.length) {
        params.categories = encodeURIComponent(filters.filters.categories.join(','));
      }
      if (filters.filters.programmes?.length) {
        params.programmes = filters.filters.programmes.join(','); // Send as comma-separated tids
      }
      // Send tids as comma-separated strings for sectors, expertise, and looking_for
      if (filters.filters.sectors?.length) {
        params.sectors = filters.filters.sectors.join(','); // Send as comma-separated tids
      }
      if (filters.filters.expertise?.length) {
        params.expertise = filters.filters.expertise.join(','); // Send as comma-separated tids
      }
      if (filters.filters.lookingFor?.length) {
        params.looking_for = filters.filters.lookingFor.join(','); // Send as comma-separated tids
      }
    }

    return await apiService.get<IdeasApiResponse>(API_ENDPOINTS.IDEAS.GET_ALL, {params});
  }

  async getIdeaById(id: string): Promise<Idea> {
    const response = await apiService.get<{data: Idea}>(API_ENDPOINTS.IDEAS.GET_BY_ID(id));
    return response.data;
  }

  async createIdea(ideaData: CreateIdeaPayload): Promise<Idea> {
    return await apiService.post<Idea>(API_ENDPOINTS.IDEAS.CREATE, ideaData);
  }

  async updateIdea(id: string, ideaData: Partial<Idea>): Promise<Idea> {
    return await apiService.patch<Idea>(API_ENDPOINTS.IDEAS.UPDATE(id), ideaData);
  }

  async toggleBookmark(ideaId: string, isBookmarked: boolean): Promise<void> {
    // Implement bookmark action for ideas
    // This might need a different endpoint - adjust as needed
    return await apiService.post('/api/v1/idea_action', {
      idea_id: parseInt(ideaId),
      action_type: 'bookmark',
      action_mode: isBookmarked ? 'remove' : 'add',
    });
  }

  async acceptDenyInvitation(actionAnswer: 'accept' | 'deny', actionId: string): Promise<void> {
    return await apiService.post(API_ENDPOINTS.IDEAS.IDEA_ACCEPT_DENY, {
      action_answer: actionAnswer,
      action_id: actionId,
    });
  }
  async acceptActionAnswer(actionAnswer: 'accept' | 'deny', actionId: string): Promise<void> {
    return await apiService.post(API_ENDPOINTS.IDEAS.IDEA_ACCEPT_DENY, {
      action_answer: actionAnswer,
      action_id: actionId,
    });
  }

  async requestJoinIdea(ideaId: string, userId: string, actionType: 'invitation' | 'request'): Promise<void> {
    return await apiService.post(API_ENDPOINTS.IDEAS.IDEA_ACTION(), {
      idea_id: parseInt(ideaId),
      user_id: parseInt(userId),
      action_type: actionType,
      action_mode: 'add',
    });
  }
  async withdrawRequest(ideaId: string): Promise<void> {
    return await apiService.post(API_ENDPOINTS.IDEAS.IDEA_ACTION(), {
      idea_id: parseInt(ideaId),
      action_type: 'request',
      action_mode: 'remove',
    });
  }
  async withdrawJoinIdea(
    ideaId: string,
    userId: string,
    actionType: 'invitation' | 'request',
    mode: 'add' | 'remove',
  ): Promise<void> {
    return await apiService.post(API_ENDPOINTS.IDEAS.IDEA_ACTION(), {
      idea_id: parseInt(ideaId),
      user_id: parseInt(userId),
      action_type: actionType,
      action_mode: mode,
    });
  }
  async leaveIdea(ideaId: string): Promise<void> {
    const patchConfig: AxiosRequestConfig = {
      headers: {
        'Content-Type': 'application/json',
      },
    };
    return await apiService.patch(
      API_ENDPOINTS.IDEAS.LEAVE,
      {
        idea_id: parseInt(ideaId),
      },
      patchConfig,
    );
  }

  async publishIdea(ideaId: string): Promise<void> {
    const patchConfig: AxiosRequestConfig = {
      headers: {
        'Content-Type': 'application/json',
      },
    };
    return await apiService.patch(API_ENDPOINTS.IDEAS.IDEA_STATUS_ACTION(ideaId, 'publish'), patchConfig);
  }
  async restoreIdea(ideaId: string): Promise<void> {
    const patchConfig: AxiosRequestConfig = {
      headers: {
        'Content-Type': 'application/json',
      },
    };
    return await apiService.patch(API_ENDPOINTS.IDEAS.IDEA_STATUS_ACTION(ideaId, 'restore'), patchConfig);
  }

  async getIdeaInvitationsRequests(): Promise<IdeaInvitationsResponse> {
    const response = await apiService.get<IdeaInvitationsResponse>(API_ENDPOINTS.IDEAS.IDEA_INVITATIONS_REQUESTS);

    return response;
  }

  async getIdeaComments(ideaId: string): Promise<any[]> {
    const response = await apiService.get<{data: any[]}>(API_ENDPOINTS.COMMENTS.GET_IDEA_COMMENTS(ideaId));
    return response.data || [];
  }

  async createIdeaComment(commentData: any): Promise<any> {
    return await apiService.post(API_ENDPOINTS.COMMENTS.CREATE_IDEA_COMMENT, commentData);
  }
}

export const ideasService = new IdeasService();
